<!-- PostViewRouter.vue -->
<script setup lang="ts">
import { app } from '@/composition/app'
import { ChannelState } from '@/composition/channels'
import { CommentState } from '@/composition/comments'
import { reportContent } from '@/composition/contentReport'
import { GroupState } from '@/composition/groups'
import { MediaState } from '@/composition/medias'
import { PostState } from '@/composition/posts'
import { ReactionState } from '@/composition/reactions'
import { UserState } from '@/composition/users'
import { checkChannelEditAccess } from '@/helper/channel.helper'
import {
  addScaleAnimation,
  scrollToCommentsSection,
} from '@/helper/comment.helper'
import {
  deletePost,
  getShareablePostLink,
  handleShareClick,
} from '@/helper/postCard.helper'
import {
  addCommentsToggleMenuItem,
  handlePostEdited,
  togglePostCommentsDisabled,
} from '@/helper/posts.helper'
import { emitter } from '@/main'
import { IChannel } from '@/models/Channels'
import { GoLiveStatus, IPosts, PostType } from '@/models/Posts'
import { ContentType, IReaction, ReactionType } from '@/models/Reaction'
import { MediaService } from '@/service/MediaService'
import { useGlobalStore } from '@/store/app'
import { isPwa } from '@ghl/clientportal-core/init'
import {
  AlertCircleIcon,
  Download01Icon,
  Edit01Icon,
  LogOut01Icon,
  Pin02Icon,
  Share07Icon,
  Trash01Icon,
} from '@gohighlevel/ghl-icons/24/outline'
import { renderIcon } from '@gohighlevel/ghl-ui'
import {
  computed,
  defineAsyncComponent,
  h,
  nextTick,
  onBeforeUnmount,
  onMounted,
  ref,
  watch,
} from 'vue'
import { useI18n } from 'vue-i18n'
import { useRoute, useRouter } from 'vue-router'
import UnpinIcon from '../../../components/common/icons/UnpinIcon.vue'
import JoinGroupModal from '../../../components/common/modal/JoinGroupModal.vue'
import UIConfirmation from '../../../components/common/modal/UIConfirmation.vue'
import { useCommunitiesToastNotification } from '../../../helper/communitiesToastNotification.helper'
import PostEditor from '../PostEditor.vue'
import PostView from './PostView.vue'
import PostViewFullPage from './PostViewFullPage.vue'

const LivePostViewIndex = defineAsyncComponent(
  () => import('../livePostView/LivePostIndex.vue')
)
const route = useRoute()
const router = useRouter()
const { t } = useI18n()
const notification = useCommunitiesToastNotification()

// Event listener will be registered in onMounted and removed in onBeforeUnmount

// Determine modal view via router meta
const isModalView = computed(() => {
  return (
    route.name === 'global-user-profile-post-view' ||
    route.meta?.openAsModal === true
  )
})

// Shared state and logic
const post = ref<any>(null)
const postId = computed(() => route.params.postId as string)
const resetPostList = ref(false)
const highlightedComment = computed(() => route.query?.commentId as string)
const highlightedCommentParentId = computed(
  () => route.query?.parentId as string
)
const fetchingComments = ref(false)
const loadingMoreComments = ref(false)
const showLikesModal = ref(false)
const showEditPostModal = ref(false)
const showDeleteConfirmationModal = ref(false)
const showReportConfirmationModal = ref(false)
const isPostLoading = ref(false)
const postChannelData = ref<IChannel | undefined>(undefined)
const showPostNotFoundModal = ref(false)
const shouldShowMovePostToChannelAction = computed(() => {
  return (
    !ChannelState.state.hasAnnouncementRestriction &&
    (checkChannelEditAccess() ||
      post.value?.user?.slug === UserState.state.currentUser?.slug) &&
    route.name !== 'global-user-profile-post-view'
  )
})
const shouldShowEditPostAction = computed(() => {
  return (
    !ChannelState.state.hasAnnouncementRestriction &&
    post.value?.user?.slug === UserState.state.currentUser?.slug &&
    route.name !== 'global-user-profile-post-view'
  )
})
const comments = computed(
  () => CommentState.state.postComments.get(post.value?._id) || []
)
const commentsNextPage = computed(() =>
  post.value
    ? CommentState.state.postCommentsNextPage.has(post.value._id)
    : false
)
const authorRole = computed(
  () => GroupState.state.groupAdmins.get(post.value?.user?.contactId)?.role
)
const channelName = computed(() => post.value?.channelName || '')

const showJoinGroupModal = ref(false)

const postActionOptions = computed(() => {
  const menuList = [
    {
      label: isPwa() ? t('common.share') : t('common.copyLink'),
      key: 'sharePost',
      class: 'rounded-2xl',
      icon() {
        return h(renderIcon(Share07Icon), null, {})
      },
    },
    {
      label: t('post.reportPost'),
      key: 'reportPost',
      class: 'rounded-2xl',
      icon() {
        return h(renderIcon(AlertCircleIcon), null, {})
      },
    },
  ]

  if (
    !app.state.isMobile &&
    (UserState.state.currentUser?.slug === post.value?.user.slug ||
      GroupState.state.isGroupAdmin) &&
    post.value?.postType === PostType.GO_LIVE &&
    post.value?.metadata?.status === GoLiveStatus.RECORDING_AVAILABLE
  ) {
    menuList.push({
      label: t('post.downloadRecording'),
      key: 'downloadRecording',
      class: 'rounded-2xl',
      icon() {
        return h(renderIcon(Download01Icon), null, {})
      },
    })
  }
  if (
    !ChannelState.state.hasAnnouncementRestriction &&
    (checkChannelEditAccess() ||
      post.value?.user?.slug === UserState.state.currentUser?.slug)
  ) {
    menuList.push({
      label: t('post.deletePost'),
      key: 'deletePost',
      class: 'rounded-2xl',
      icon() {
        return h(renderIcon(Trash01Icon), null, {})
      },
    })
  }
  if (shouldShowMovePostToChannelAction.value) {
    menuList.push({
      label: t('post.movePostToChannel'),
      key: 'movePostToChannel',
      class: 'rounded-2xl',
      icon() {
        return h(renderIcon(LogOut01Icon), null, {})
      },
    })
  }
  if (shouldShowEditPostAction.value) {
    menuList.push({
      label: t('post.editPost'),
      key: 'editPost',
      class: 'rounded-2xl',
      icon() {
        return h(renderIcon(Edit01Icon), null, {})
      },
    })
  }

  // Add enable/disable comments option if user is admin or post author
  addCommentsToggleMenuItem(
    menuList,
    post.value?.user?.slug,
    post.value?.isCommentDisabled,
    t
  )

  if (
    GroupState.state.isGroupAdmin &&
    postChannelData.value?.visibility === 'PUBLIC'
  ) {
    if (post.value?.channelPinnedIndex == undefined) {
      menuList.push({
        label: t('post.pinToChannel'),
        key: 'pinToChannel',
        class: 'rounded-2xl',
        icon() {
          return h(renderIcon(Pin02Icon), null, {})
        },
      })
    } else {
      menuList.push({
        label: t('post.unpinFromChannel'),
        key: 'unpinFromChannel',
        class: 'rounded-2xl h-6 w-6',
        icon() {
          return h(renderIcon(UnpinIcon), null, {})
        },
      })
    }
    if (post.value?.homePinnedIndex == undefined) {
      menuList.push({
        label: t('post.pinToHome'),
        key: 'pinToHome',
        class: 'rounded-2xl',
        icon() {
          return h(renderIcon(Pin02Icon), null, {})
        },
      })
    } else {
      menuList.push({
        label: t('post.unpinFromHome'),
        key: 'unpinFromHome',
        class: 'rounded-2xl',
        icon() {
          return h(renderIcon(UnpinIcon), null, {})
        },
      })
    }
  }

  return menuList
})

async function fetchAndInit() {
  try {
    isPostLoading.value = true
    post.value = await PostState.fetchPost(postId.value)
    console.log('[fetchAndInit] post', post.value)
    if (!post.value) {
      showPostNotFoundModal.value = true
      return
    }
    postChannelData.value = ChannelState.setCurrentChannel(
      post.value?.channelSlug
    )
    isPostLoading.value = false

    if (
      post.value?.postType === PostType.GO_LIVE &&
      [GoLiveStatus.STREAM_STARTED, GoLiveStatus.STREAM_COMPLETED].includes(
        post.value?.metadata?.status as GoLiveStatus
      ) &&
      !GroupState.isActiveMember()
    ) {
      showJoinGroupModal.value = true
      isPostLoading.value = true
    }
  } catch (error) {
    console.log('[fetchAndInit] error fetching post', error)
    showPostNotFoundModal.value = true
    isPostLoading.value = false
  }
  try {
    // Skip fetching comments if post has 0 comments and no highlighted comment
    if (post.value.commentsCount === 0 && !highlightedComment.value) {
      // Initialize empty comments array in state to prevent future fetches
      if (!CommentState.state.postComments.has(postId.value)) {
        CommentState.state.postComments.set(postId.value, [])
      }
      fetchingComments.value = false
      return
    }

    fetchingComments.value = true
    if (!CommentState.state.postComments.get(postId.value)) {
      await CommentState.fetchComments(postId.value, { limit: 10 })
    }
    if (highlightedComment.value) {
      await CommentState.fetchCommentById(
        postId.value,
        highlightedComment.value,
        highlightedCommentParentId.value
      )
    }
    fetchingComments.value = false
  } catch (error) {
    console.log('[fetchAndInit] error fetching comments', error)
  }
}

onMounted(() => {
  fetchAndInit()
  // this case is for live post view we will get this params
  if (route.query?.resetPostList === 'true') {
    resetPostList.value = true
    router.replace({ query: { ...route.query, resetPostList: undefined } })
  }
  emitter.on('currentGroup:channelsSet', () => {
    console.log('[fetchAndInit] channels set')
    fetchAndInit()
  })
  // Register close-postview event listener
  emitter.on('close-postview', closePostView)
  emitter.on('add-comment', addComment)
  emitter.on('delete-comment', deleteComment)
})

onBeforeUnmount(() => {
  if (highlightedComment.value) {
    CommentState.unhighlightComment(
      postId.value,
      highlightedComment.value,
      highlightedCommentParentId.value
    )
  }
  MediaState.joinAllStreams()
  // remove event listeners
  emitter.off('currentGroup:channelsSet')
  emitter.off('close-postview')
  emitter.off('add-comment')
  emitter.off('delete-comment')
})

function closePostView() {
  // Determine the expected destination route
  let expectedRoute: {
    name: string
    params: any
    fullPath: string
    query?: any
  } | null = null

  if (route.params.channelSlug) {
    if (route.name === 'channel-search-post-view') {
      expectedRoute = {
        name: 'channel-search',
        params: { channelSlug: route.params.channelSlug },
        query: { q: route.query.q },
        fullPath: `/communities/groups/${route.params.groupSlug}/channels/${route.params.channelSlug}/search?q=${route.query.q}`,
      }
    } else {
      expectedRoute = {
        name: 'channel-overview',
        params: { channelSlug: route.params.channelSlug },
        fullPath: `/communities/groups/${route.params.groupSlug}/channels/${route.params.channelSlug}`,
      }
    }
  } else if (route.params.userSlug) {
    expectedRoute = {
      name: 'global-user-profile',
      params: { userSlug: route.params.userSlug },
      query: { groupId: route.query.groupId },
      fullPath: `/communities/users/${route.params.userSlug}?groupId=${route.query.groupId}`,
    }
  } else if (route.params.groupSlug) {
    if (route.name === 'group-search-post-view') {
      expectedRoute = {
        name: 'group-home-search',
        params: { groupSlug: route.params.groupSlug },
        query: { q: route.query.q },
        fullPath: `/communities/groups/${route.params.groupSlug}/search?q=${route.query.q}`,
      }
    } else {
      expectedRoute = {
        name: 'home',
        params: { groupSlug: route.params.groupSlug },
        fullPath: `/communities/groups/${route.params.groupSlug}/home`,
      }
    }
  } else {
    expectedRoute = {
      name: 'home',
      params: { groupSlug: route.params.groupSlug },
      fullPath: `/communities/groups/${route.params.groupSlug}/home`,
    }
  }

  // Check if current route is already the same as expected route
  const currentPath = route.path || route.fullPath
  const expectedPath = expectedRoute?.fullPath

  if (currentPath === expectedPath) {
    return
  }

  // Check if we can safely go back by comparing previous route with expected route
  if (window.history.length <= 1) {
    router.replace({
      name: expectedRoute?.name,
      params: expectedRoute?.params,
      query: expectedRoute?.query,
    })
  } else if (canSafelyGoBackToRoute(expectedRoute)) {
    router.back()
  } else {
    router.replace({
      name: expectedRoute?.name,
      params: expectedRoute?.params,
      query: expectedRoute?.query,
    })
  }
}

function canSafelyGoBackToRoute(expectedRoute: any): boolean {
  if (!expectedRoute) return false

  // Check if we have history state with previous route information
  const historyState = window.history?.state
  const previousRoute = historyState?.back

  // If we don't have previous route info, be conservative
  if (!previousRoute) {
    return false
  }

  // Get the expected path from the route object
  const expectedPath = expectedRoute.path || expectedRoute.fullPath

  // Check if the previous route matches our expected path exactly
  const routesMatch =
    previousRoute === expectedPath || previousRoute.endsWith(expectedPath)

  return routesMatch
}

// Common comment add/delete handlers (works for both modal and full page)

function addComment(comment: any) {
  if (!post.value) return
  const isPostAlreadyInList = PostState.state.posts.find(
    (fetchedPost: any) => fetchedPost._id === post.value._id
  )

  // Update the count only if post is not already in the list, if in list it will automatically update via addComment method in composition
  if (!isPostAlreadyInList) {
    post.value.commentsCount = (post.value.commentsCount || 0) + 1
  }

  // Scroll to show the new comment for better UX
  if (!comment?.parentId) {
    // For top-level comments, scroll to the top of the comments section
    nextTick(async () => {
      // Scroll to comments section
      await scrollToCommentsSection()

      // If we have the comment ID, add a visual effect
      if (comment?._id) {
        setTimeout(() => {
          const newCommentElement = document.getElementById(comment._id)
          if (newCommentElement) {
            addScaleAnimation(newCommentElement, {
              initialScale: 0.98,
              finalScale: 1,
              duration: 300,
            })
          }
        }, 500) // Extra delay to ensure scroll completes first
      }
    })
  }
}

function deleteComment(comment) {
  if (!post.value) return
  const isPostAlreadyInList = PostState.state.posts.find(
    (fetchedPost: any) => fetchedPost._id === post.value._id
  )

  // UPdate the count only if post is not already in the list, if in list it will automatically update via deleteComment method in composition
  if (!isPostAlreadyInList) {
    let commentCounter = 1
    commentCounter += comment.repliesCount // Deleting parent will delete all child as well
    post.value.commentsCount = Math.max(
      0,
      (post.value.commentsCount || 0) - commentCounter
    )
  }
}

watch(
  () => fetchingComments.value,
  newValue => {
    if (newValue === false && highlightedComment.value) {
      CommentState.highlightComment(
        postId.value,
        highlightedComment.value,
        highlightedCommentParentId.value
      )
    }
  }
)

async function likePost() {
  if (
    !UserState.state?.currentUser?.contactId ||
    !GroupState.isActiveMember()
  ) {
    showJoinGroupModal.value = true
    return
  }
  const payload: IReaction = {
    contentId: post.value._id,
    contentType: ContentType.POST,
    reactionType: ReactionType.LIKE,
    createdBy: UserState.state.currentUser.contactId,
    channelSlug: post.value.channelSlug,
  }
  const isAddReaction = await ReactionState.toggleReaction(payload, post.value)
  if (isAddReaction != undefined) {
    await ReactionState.addOrDeleteReaction(payload, post.value, isAddReaction)
  }
}

async function loadMoreComments() {
  try {
    loadingMoreComments.value = true
    await CommentState.fetchComments(postId.value, {
      previousId: CommentState.state.postCommentsNextPage.get(postId.value),
      limit: 30,
    })
  } finally {
    loadingMoreComments.value = false
  }
}

async function sharePost() {
  if (isPwa()) {
    const link = getShareablePostLink(post.value, router)
    await navigator?.clipboard?.writeText?.(link).catch(() => {})
    notification.create({
      title: t('common.linkCopied'),
      type: 'success',
      duration: 3000,
    })
  } else {
    await handleShareClick(post.value, router)
    notification.create({
      title: t('common.linkCopied'),
      type: 'success',
      duration: 3000,
    })
  }
}

async function handleActionSelected(action: string) {
  switch (action) {
    case 'deletePost':
      showDeleteConfirmationModal.value = true
      break
    case 'pinToChannel':
      const pinnedIndex = await PostState.pinPostToChannelTimeline(
        post.value._id,
        post.value.channelSlug
      )
      post.value.channelPinnedIndex = pinnedIndex
      await fetchAndInit()
      notification.create({
        title: t('post.notifications.pinned'),
        type: 'success',
        duration: 3000,
      })
      break
    case 'unpinFromChannel':
      const unpinnedIndex = await PostState.unpinPostFromChannelTimeline(
        post.value._id,
        post.value.channelSlug
      )
      post.value.channelPinnedIndex = unpinnedIndex
      await fetchAndInit()
      notification.create({
        title: t('post.notifications.unpinned'),
        type: 'success',
        duration: 3000,
      })
      break
    case 'pinToHome':
      await PostState.pinPostToHomeTimeline(
        post.value._id,
        post.value.channelId
      )
      await fetchAndInit()
      notification.create({
        title: t('post.notifications.pinned'),
        type: 'success',
        duration: 3000,
      })
      break
    case 'unpinFromHome':
      await PostState.unpinPostFromHomeTimeline(
        post.value._id,
        post.value.channelId
      )
      await fetchAndInit()
      notification.create({
        title: t('post.notifications.unpinned'),
        type: 'success',
        duration: 3000,
      })
      break
    case 'editPost':
      showEditPostModal.value = true
      break
    case 'enableComments':
    case 'disableComments':
      await togglePostCommentsDisabled(post.value, notification, t)
      break
    case 'sharePost':
      await sharePost()
      break
    case 'reportPost':
      showReportConfirmationModal.value = true
      break
    case 'movePostToChannel':
      emitter.emit('open-move-post-to-channel-modal', post.value)
      break
    case 'downloadRecording':
      const signedUrl = await MediaService.getDownloadSignedUrl(
        post.value?.metadata?.licenseId as string
      )
      window.open(signedUrl.signedUrl, '_blank')
      break
  }
}

async function reportPost(post: IPosts) {
  try {
    await reportContent(
      post._id as string,
      ContentType.POST,
      useGlobalStore().$state.groupId,
      useGlobalStore().$state.locationId,
      UserState.state?.currentUser?.contactId || '',
      UserState.state?.currentUser?.fullName || ''
    )
    notification.create({
      title: t('post.notifications.reported'),
      type: 'success',
      duration: 3000,
    })
  } catch {
    notification.create({
      title: 'Something went wrong!',
      type: 'error',
      duration: 3000,
    })
  } finally {
    showReportConfirmationModal.value = false
  }
}

function closeEditPostModal() {
  showEditPostModal.value = false
}

function updatePostInPostView(updatedPost) {
  // Update the local ref post with the updated post
  const newPost = { ...post.value, ...updatedPost }
  post.value = newPost
}

function handlePostNotFoundClose(showStatus: boolean) {
  showPostNotFoundModal.value = showStatus
  if (showStatus === false) {
    closePostView()
  }
}
</script>

<template>
  <div>
    <LivePostViewIndex
      v-if="post?.postType === PostType.GO_LIVE && [GoLiveStatus.STREAM_STARTED, GoLiveStatus.STREAM_COMPLETED].includes(post?.metadata?.status as GoLiveStatus) && GroupState.isActiveMember()"
      :post="post"
      :reset-post-list="resetPostList"
    />
    <PostView
      v-else-if="isModalView || app.state.isMobile"
      :post="post"
      :author-role="authorRole"
      :channel-name="channelName"
      :comments="comments"
      :fetching-comments="fetchingComments"
      :loading-more-comments="loadingMoreComments"
      :comments-next-page="commentsNextPage"
      :show-likes-modal="showLikesModal"
      :post-action-options="postActionOptions"
      @like="likePost"
      @load-more-comments="loadMoreComments"
      @likes-clicked="() => (showLikesModal = true)"
      @action-selected="handleActionSelected"
    />
    <PostViewFullPage
      v-else
      :post="post"
      :author-role="authorRole"
      :channel-name="channelName"
      :comments="comments"
      :is-post-loading="isPostLoading"
      :fetching-comments="fetchingComments"
      :loading-more-comments="loadingMoreComments"
      :comments-next-page="commentsNextPage"
      :show-likes-modal="showLikesModal"
      :post-action-options="postActionOptions"
      @like="likePost"
      @load-more-comments="loadMoreComments"
      @likes-clicked="() => (showLikesModal = true)"
      @action-selected="handleActionSelected"
    />
    <JoinGroupModal
      :show="showJoinGroupModal"
      @update:show="
        () => {
          showJoinGroupModal = false
          if (post.value?.postType === PostType.GO_LIVE) {
            isPostLoading = false
          }
        }
      "
    />
    <PostEditor
      v-if="showEditPostModal"
      :show-editor="showEditPostModal"
      :auto-focus="true"
      :post="post"
      @update:show="closeEditPostModal"
      @update:edited="
        updatedPost => {
          updatePostInPostView(updatedPost)
          handlePostEdited(updatedPost)
        }
      "
    />
    <UIConfirmation
      :show="showDeleteConfirmationModal"
      type="error"
      :title="t('post.deletePost')"
      button-type="error"
      :message="t('post.deletePostConfirmation')"
      @update:show="showDeleteConfirmationModal = $event"
      @update:confirmed="deletePost(post)"
    />
    <UIConfirmation
      :show="showReportConfirmationModal"
      type="error"
      :title="t('post.reportPost')"
      button-type="error"
      :message="t('post.reportPostConfirmation')"
      @update:show="showReportConfirmationModal = $event"
      @update:confirmed="reportPost(post)"
    />
    <UIConfirmation
      v-if="showPostNotFoundModal"
      :show="showPostNotFoundModal"
      :show-actions="false"
      type="error"
      :title="t('post.postNotFound')"
      button-type="error"
      :message="t('post.postNotFoundMessage')"
      @update:show="handlePostNotFoundClose"
    />
  </div>
</template>
